import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowUpLine} from '@dropbox/dig-icons/assets';
import {useQueryClient} from '@tanstack/react-query';
import {activeCommentAtom, commentDraftDirtyAtom, pendingDeleteCommentIdsAtom} from 'atoms/comment';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {derivedThemeAtom} from 'atoms/layout';
import {snackbarAtom} from 'atoms/snackbar';
import {CommentService, DocumentThread} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {useCollabId, usePlanningId} from 'components/DSYS/CollabProvider';
import styles from 'components/DSYS/editor/plugins/Comment.module.css';
import {
  COMMENT_DISPLAY_WIDTH_PX,
  getInlineCommentBoxMaxHeight,
} from 'components/DSYS/editor/plugins/commentDisplaySizing';
import {CommentBox} from 'components/DSYS/editor/plugins/comments/CommentBox';
import {CommentDisplayPreview} from 'components/DSYS/editor/plugins/comments/CommentDisplayPreview';
import {getCommentRenderState} from 'components/DSYS/editor/plugins/comments/commentRenderState';
import {useDocumentThreadService} from 'components/DSYS/editor/plugins/comments/useThreads';
import mentionStyles from 'components/DSYS/editor/plugins/Mention.module.css';
import {
  EmployeeMentionTypeaheadOption,
  MentionTypeaheadOption,
} from 'components/DSYS/editor/plugins/MentionPlugin';
import {findMentions} from 'components/DSYS/editor/utils/findMentions';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {sanitizeCommentContent} from 'helpers/commentContent';
import {hasMeaningfulCommentDraft} from 'helpers/commentDraft';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getService} from 'utilities';
import {isPrivateThread} from 'views/workstreams/utils';

import {useCommentDisplayClose} from './useCommentDisplayClose';

const COLLAB_ACTIVITY_STATE_EVENT = 'pulse:collab-activity-state';

const publishCollabActivityState = (state: 'active' | 'commenting', collabId?: string) => {
  window.dispatchEvent(
    new CustomEvent(COLLAB_ACTIVITY_STATE_EVENT, {
      detail: {collabId, state},
    })
  );
};

type CommentDisplayActiveProps = {
  mode?: 'active';
  onCreate?: (commentText: string) => Promise<string | undefined>;
  thread?: DocumentThread;
  onClose?: () => void;
  onCommentCreated?: (threadId: string) => void;
  // Used for comments shown inline in the editor (Workstream app)
  maxHeightForInlineComment?: number;
  // Used for comments shown on right rail (Planning app)
  commentBoxMaxHeight?: CSSProperties['maxHeight'];
  autoFocusInput?: boolean;
  closeOnScroll?: boolean;
  commentMentions?: MentionTypeaheadOption[];
};

type CommentDisplayPreviewProps = {
  mode: 'preview';
  thread?: DocumentThread;
  compact?: boolean;
};

type CommentDisplayProps = CommentDisplayActiveProps | CommentDisplayPreviewProps;

const CommentDisplayActive = ({
  onCreate,
  thread,
  onClose,
  onCommentCreated,
  maxHeightForInlineComment,
  commentBoxMaxHeight,
  autoFocusInput = true,
  closeOnScroll = true,
  commentMentions: providedCommentMentions,
}: Omit<CommentDisplayActiveProps, 'mode'>) => {
  const {employee, reportingLine} = useAtomValue(loggedInEmployeeAtom);
  const activeComment = useAtomValue(activeCommentAtom);
  const pendingDeleteCommentIds = useAtomValue(pendingDeleteCommentIdsAtom);
  const mode = useAtomValue(derivedThemeAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const setCommentDraftDirty = useSetAtom(commentDraftDirtyAtom);
  const collabId = useCollabId();
  const planningId = usePlanningId();
  const queryClient = useQueryClient();
  const {updateThread: updateDocumentThread} = useDocumentThreadService();

  const isInPlanningDoc = Boolean(planningId); // Comment behavior is different in Planning doc vs. Workstream doc
  // New comments are always public; existing private threads keep their private styling.
  const isPrivate = isPrivateThread(thread);

  const editorRef = useRef<{clearEditor: () => void}>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const replyInputBodyRef = useRef<HTMLDivElement | null>(null);
  const replyInputEditorRef = useRef<HTMLDivElement | null>(null);
  const previousReplyInputLengthRef = useRef(0);

  // Local state for comment input
  const [commentText, setCommentText] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [localReplyScrollRequest, setLocalReplyScrollRequest] = useState<{
    threadId: string;
    commentId: string;
  } | null>(null);
  const sanitizedCommentText = useMemo(() => sanitizeCommentContent(commentText), [commentText]);
  const hasUnsavedDraft = hasMeaningfulCommentDraft(sanitizedCommentText);
  const inlineDisplayMaxHeight =
    commentBoxMaxHeight === undefined ? maxHeightForInlineComment : undefined;
  const inlineCommentBoxMaxHeight =
    commentBoxMaxHeight === undefined
      ? getInlineCommentBoxMaxHeight(maxHeightForInlineComment)
      : undefined;
  const inlineReplyInputMaxHeight =
    inlineDisplayMaxHeight !== undefined && inlineCommentBoxMaxHeight !== undefined
      ? Math.max(22, inlineDisplayMaxHeight - inlineCommentBoxMaxHeight - 24)
      : 250;
  const replyInputMaxHeight =
    thread || activeComment?.optimisticComment
      ? inlineReplyInputMaxHeight
      : (maxHeightForInlineComment ?? 274) - 24;
  const {isDeletingLastComment} = useMemo(
    () => getCommentRenderState(thread?.comments, pendingDeleteCommentIds),
    [pendingDeleteCommentIds, thread?.comments]
  );

  useEffect(() => {
    publishCollabActivityState('commenting', collabId);

    return () => {
      publishCollabActivityState('active', collabId);
    };
  }, [collabId]);

  const updateReplyInputWrapHeight = useCallback((options?: {allowShrink?: boolean}) => {
    const body = replyInputBodyRef.current;
    const editorHost = replyInputEditorRef.current;
    const editor = editorHost?.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!body || !editor) {
      return;
    }

    // Empty and single-line replies should keep the compact vertically-centered layout.
    // The float only becomes useful once the reply spans multiple lines.
    const hasContent = Boolean(editor.textContent?.trim());
    const lastBlock = editor.lastElementChild as HTMLElement | null;
    const contentHeight = lastBlock ? lastBlock.offsetTop + lastBlock.offsetHeight : 0;
    const lineHeight =
      Number.parseFloat(window.getComputedStyle(lastBlock ?? editor).lineHeight) || 22;
    const isSingleLine = contentHeight <= lineHeight + 2;

    // Multi-line replies use a CSS float whose height matches the actual content height,
    // so wrapping only happens near the end of the text instead of throughout the scroll area.
    const wrapHeight = hasContent && !isSingleLine ? Math.max(40, contentHeight || 40) : 0;

    // A one-line reply cannot use the float yet, but it still needs to reserve button space
    // so typed text wraps before it can sit underneath the submit/privacy buttons.
    const singleLineEndPadding =
      hasContent && isSingleLine ? 'var(--comment-reply-actions-width)' : '0px';
    const currentWrapHeight = Number.parseFloat(
      body.style.getPropertyValue('--comment-reply-content-height')
    );

    // While typing forward, only let the float height grow. That avoids one-keystroke
    // layout oscillation near max height; deletion is allowed to shrink it again.
    const stableWrapHeight =
      !options?.allowShrink && Number.isFinite(currentWrapHeight)
        ? Math.max(currentWrapHeight, wrapHeight)
        : wrapHeight;
    const nextWrapHeight = `${stableWrapHeight}px`;

    const currentSingleLineEndPadding = body.style.getPropertyValue(
      '--comment-reply-single-line-end-padding'
    );

    if (
      body.style.getPropertyValue('--comment-reply-content-height') === nextWrapHeight &&
      currentSingleLineEndPadding === singleLineEndPadding
    ) {
      return;
    }

    body.style.setProperty('--comment-reply-content-height', nextWrapHeight);
    body.style.setProperty('--comment-reply-single-line-end-padding', singleLineEndPadding);
  }, []);

  const commentMentions = useMemo(() => {
    if (providedCommentMentions) {
      return providedCommentMentions;
    }

    const allEmployees = [...reportingLine.slice(1)];

    const uniqueEmployees = Array.from(
      new Map(allEmployees.map((emp) => [emp.user_id, emp])).values()
    );

    return uniqueEmployees
      .map((emp) => new EmployeeMentionTypeaheadOption(emp))
      .filter((emp) => emp.id !== employee.user_id);
  }, [providedCommentMentions, reportingLine, employee.user_id]);

  useEffect(() => {
    updateReplyInputWrapHeight();
  }, [replyInputMaxHeight, updateReplyInputWrapHeight]);

  useEffect(() => {
    setCommentDraftDirty(hasUnsavedDraft);

    return () => {
      setCommentDraftDirty(false);
    };
  }, [hasUnsavedDraft, setCommentDraftDirty]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedDraft) return;

      event.preventDefault();
      const message = t('get_rid_of_comment_you_wrote');
      // Legacy browsers may require setting returnValue.
      // eslint-disable-next-line no-param-reassign, deprecation/deprecation
      event.returnValue = message;
      return message;
    };

    if (hasUnsavedDraft) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedDraft]);

  const {closeCommentDisplay, confirmDiscardDraft, handleCloseComment} = useCommentDisplayClose({
    hasUnsavedDraft,
    onClose,
    setCommentDraftDirty,
  });

  // Handle thread resolution
  const handleResolveThread = useCallback(
    async (isResolved: boolean) => {
      if (!thread) return;
      if (isResolved && !confirmDiscardDraft()) return;

      try {
        await updateDocumentThread({
          threadId: thread.id,
          data: {
            ...thread,
            is_resolved: isResolved,
          },
        });

        // Optimistically resolve the thread in document threads
        queryClient.setQueryData(
          ['document', 'threads', thread.document_id],
          (old: DocumentThread[] | undefined) => {
            return old?.map((th) => {
              if (th.id === thread.id) {
                return {...th, is_resolved: isResolved};
              }
              return th;
            });
          }
        );

        if (isResolved) {
          // Close the comment display when resolved
          closeCommentDisplay();
        }
      } catch (error) {
        setSnackbarMessage({text: 'Error updating thread: ' + (error as Error).message});
      }
    },
    [
      thread,
      updateDocumentThread,
      queryClient,
      setSnackbarMessage,
      confirmDiscardDraft,
      closeCommentDisplay,
    ]
  );

  // Close comment display if thread has no active comments
  useEffect(() => {
    if (thread) {
      const hasActiveComments = thread.comments?.some((comment) => !comment.is_deleted);
      if (!hasActiveComments && confirmDiscardDraft()) {
        closeCommentDisplay();
      }
    }
  }, [thread, closeCommentDisplay, confirmDiscardDraft]);

  const resolvedCommentBoxMaxHeight = commentBoxMaxHeight ?? inlineCommentBoxMaxHeight;
  const planningReplyScrollRequestKey =
    isInPlanningDoc && localReplyScrollRequest && localReplyScrollRequest.threadId === thread?.id
      ? localReplyScrollRequest.commentId
      : undefined;

  // Handle comment deletion - only close if it was the last comment
  const handleDeleteComment = useCallback(() => {
    const activeComments = thread?.comments?.filter((comment) => !comment.is_deleted) ?? [];
    // If there's only 1 active comment (the one being deleted), close the display
    if (activeComments.length <= 1 && confirmDiscardDraft()) {
      closeCommentDisplay();
    }
  }, [thread, closeCommentDisplay, confirmDiscardDraft]);

  const handleSubmit = useCallback(async () => {
    if (!sanitizedCommentText.trim() || isDeletingLastComment) return;

    setIsCreating(true);

    if (thread) {
      // Extract mentioned user IDs from comment text
      const extractedMentions = findMentions(sanitizedCommentText);
      const mentionedUserIds = extractedMentions.map((mention) => mention.id);

      // Handle reply to existing thread
      try {
        const newComment = await getService(CommentService).createCommentReplyApiV1CommentReplyPost(
          {
            content: sanitizedCommentText,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            document_thread_id: thread.id,
            mentioned_user_ids: mentionedUserIds,
          }
        );

        setCommentText('');
        editorRef.current?.clearEditor();

        if (collabId) {
          // Optimistically add the new comment to the document thread.
          queryClient.setQueryData(
            ['document', 'threads', collabId],
            (old: DocumentThread[] | undefined) => {
              if (!old?.length) return old;
              return old.map((th) => {
                if (th.id === newComment.document_thread_id) {
                  // If the thread already has the new comment, do not optimistically add it again
                  if (th.comments?.some((comment) => comment.id === newComment.id)) return th;
                  return {...th, comments: [...(th.comments ?? []), newComment]};
                }
                return th;
              });
            }
          );
        }
        setLocalReplyScrollRequest({threadId: thread.id, commentId: newComment.id});

        // Invalidate the thread query to refresh the comments
        queryClient.invalidateQueries({
          queryKey: ['document', 'thread', thread.id],
        });
        queryClient.invalidateQueries({
          queryKey: ['document', 'threads', collabId],
        });
      } catch (error) {
        setSnackbarMessage({text: 'Error adding reply: ' + (error as Error).message});
      } finally {
        setIsCreating(false);
      }
    } else {
      // Handle creating new comment
      try {
        setCommentDraftDirty(false);
        const threadId = await onCreate?.(sanitizedCommentText);

        if (!threadId) {
          setCommentDraftDirty(hasUnsavedDraft);
          return;
        }

        setCommentText('');
        editorRef.current?.clearEditor();

        // Optimistically open the comment thread if we got a threadId
        if (onCommentCreated) {
          onCommentCreated(threadId);
        }

        // Optimistically add the new thread/comment to document threads
        if (collabId) {
          const now = new Date().toISOString();
          const newThread: DocumentThread = {
            document_id: collabId,
            id: threadId,
            created_at: now,
            comments: [
              {
                content: sanitizedCommentText,
                id: `temp-optimistic-thread__${threadId}`, // Assign a temporary ID
                created_at: now,
                updated_at: now,
                is_deleted: false,
                employee,
              },
            ],
            employee,
            is_resolved: false,
          };

          queryClient.setQueryData(
            ['document', 'threads', collabId],
            (old: DocumentThread[] | undefined) => {
              if (!old?.length) return [newThread];
              // If a refetch already added this thread, don't append an optimistic duplicate
              if (old.some((th) => th.id === newThread.id)) return old;
              return [...old, newThread];
            }
          );
        }
      } catch (error) {
        setCommentDraftDirty(hasUnsavedDraft);
        setSnackbarMessage({text: 'Error creating comment: ' + (error as Error).message});
      } finally {
        setIsCreating(false);
      }
    }
  }, [
    sanitizedCommentText,
    hasUnsavedDraft,
    thread,
    onCreate,
    setCommentText,
    setCommentDraftDirty,
    setSnackbarMessage,
    queryClient,
    collabId,
    onCommentCreated,
    employee,
    isDeletingLastComment,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleSubmit]);

  // Handle outside clicks to close the comment thread (only when we have a thread)
  // Include styles.comment to ignore clicks on highlighted text (which opens this component)
  useOutsideClick(
    toolbarRef,
    handleCloseComment,
    [mentionStyles.container, 'focused', styles.editor, styles.comment],
    true,
    'mousedown',
    // ignore clicks on highlighted text & comments panel preview to switch to that comment
    ['[data-highlight-id]', '[data-comments-panel-preview-thread-id]', '#emoji-picker'],
    true
  );

  // Close comment on user scroll (including inside scrollable parents)
  useEffect(() => {
    if (!closeOnScroll || !handleCloseComment || isInPlanningDoc) return;

    const USER_INTERACTION_TIMEOUT_MS = 250;
    const CLOSE_THRESHOLD_PX = 150;

    const userInteractingRef = {current: false};
    const userTimerRef = {current: null as number | null};
    const accumulatedRef = {current: 0};
    const lastPosRef = {current: new Map<EventTarget | Window, number>()};

    const markUserInteraction = () => {
      userInteractingRef.current = true;
      if (userTimerRef.current != null) window.clearTimeout(userTimerRef.current);
      userTimerRef.current = window.setTimeout(() => {
        userInteractingRef.current = false;
        userTimerRef.current = null;
        accumulatedRef.current = 0;
      }, USER_INTERACTION_TIMEOUT_MS);
    };

    const handleWheel = () => markUserInteraction();
    const handleTouchStart = () => markUserInteraction();
    const handlePointerDown = () => markUserInteraction();

    const handleScroll = (ev: Event) => {
      // If scroll wasn't triggered by user interaction, ignore it.
      // This prevents toolbar from closing when we auto-scroll comments into view.
      if (!userInteractingRef.current) {
        const scroller = ev.currentTarget as EventTarget | Window;
        const pos = scroller === window ? window.scrollY : (scroller as Element).scrollTop;
        lastPosRef.current.set(scroller, pos);
        return;
      }

      const scroller = ev.currentTarget as EventTarget | Window;
      const pos = scroller === window ? window.scrollY : (scroller as Element).scrollTop;
      const prev = lastPosRef.current.get(scroller) ?? pos;
      const dy = Math.abs(pos - prev);
      lastPosRef.current.set(scroller, pos);

      accumulatedRef.current += dy;

      if (accumulatedRef.current >= CLOSE_THRESHOLD_PX) {
        accumulatedRef.current = 0;
        if (userTimerRef.current != null) {
          window.clearTimeout(userTimerRef.current);
          userTimerRef.current = null;
        }
        userInteractingRef.current = false;
        handleCloseComment();
      }
    };

    const isScrollable = (el: HTMLElement | null) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      const canScroll = el.scrollHeight > el.clientHeight;
      return canScroll && /auto|scroll|overlay/.test(overflowY);
    };

    const getScrollableParents = (el: HTMLElement | null) => {
      const parents: (HTMLElement | Window)[] = [];
      let node: HTMLElement | null = el;
      while (node) {
        if (isScrollable(node)) parents.push(node);
        node = node.parentElement;
      }
      // Always include window as fallback
      parents.push(window);
      return parents;
    };

    const activeCommentEl = document.querySelector(
      `[data-highlight-id~="${activeComment?.threadId}"]`
    ) as HTMLElement | null;
    if (!activeCommentEl) {
      return;
    }

    // Attach listeners to each scroll parent
    const scrollableParents = getScrollableParents(activeCommentEl);
    scrollableParents.forEach((parent) => {
      const target: any = parent === window ? window : parent;
      const initialPos = target === window ? window.scrollY : (target as Element).scrollTop;
      lastPosRef.current.set(target, initialPos);
      target.addEventListener('scroll', handleScroll, {passive: true});
    });

    window.addEventListener('wheel', handleWheel, {passive: true});
    window.addEventListener('touchstart', handleTouchStart, {passive: true});
    window.addEventListener('pointerdown', handlePointerDown, {passive: true});

    return () => {
      scrollableParents.forEach((parent) => {
        const target: any = parent === window ? window : parent;
        target.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('pointerdown', handlePointerDown);
      if (userTimerRef.current != null) {
        window.clearTimeout(userTimerRef.current);
      }
    };
  }, [activeComment?.threadId, closeOnScroll, handleCloseComment, isInPlanningDoc]);

  return (
    <Box
      ref={toolbarRef}
      className={styles.editor}
      boxShadow="Floating"
      backgroundColor="Background Base"
      borderRadius="Large"
      data-comment-box="true"
      style={{
        width: COMMENT_DISPLAY_WIDTH_PX,
        outlineStyle: 'solid',
        outlineWidth: isPrivate ? 3 : 1,
        outlineColor: isPrivate
          ? 'var(--dig-color__secondary__base)'
          : 'var(--dig-color__border__subtle)',
        maxHeight: inlineDisplayMaxHeight,
        overflow: inlineDisplayMaxHeight === undefined ? undefined : 'hidden',
        display: inlineDisplayMaxHeight === undefined ? undefined : 'flex',
        flexDirection: inlineDisplayMaxHeight === undefined ? undefined : 'column',
      }}
    >
      {/* Display existing comments (or optimistic comment if thread hasn't loaded yet) */}
      {(thread || activeComment?.optimisticComment) && (
        <>
          <CommentBox
            thread={
              thread ?? {
                // Create a fake thread for optimistic display
                id: activeComment?.threadId ?? '',
                document_id: collabId ?? '',
                created_at: activeComment?.optimisticComment?.createdAt ?? new Date().toISOString(),
                is_resolved: false,
                employee: {
                  user_id: activeComment?.optimisticComment?.authorId ?? employee.user_id,
                  ldap: employee.ldap,
                  name: activeComment?.optimisticComment?.authorName ?? employee.name,
                  role: null,
                  level: null,
                },
                comments: [
                  {
                    id: 'optimistic-comment',
                    content: activeComment?.optimisticComment?.text ?? '',
                    created_at:
                      activeComment?.optimisticComment?.createdAt ?? new Date().toISOString(),
                    updated_at:
                      activeComment?.optimisticComment?.createdAt ?? new Date().toISOString(),
                    is_deleted: false,
                    employee: {
                      user_id: activeComment?.optimisticComment?.authorId ?? employee.user_id,
                      ldap: employee.ldap,
                      name: activeComment?.optimisticComment?.authorName ?? employee.name,
                      role: null,
                      level: null,
                    },
                  },
                ],
                private_employees: null,
                private_teams: null,
              }
            }
            onResolveClick={handleResolveThread}
            onDeleteComment={handleDeleteComment}
            maxHeight={resolvedCommentBoxMaxHeight}
            enableCommentLinkCopy={isInPlanningDoc}
            showFirstCommentOnOpen={isInPlanningDoc}
            scrollToBottomRequestKey={planningReplyScrollRequestKey}
          />
          {/* Border between comments and reply input */}
          <Box borderTop="Solid" borderColor="Border Subtle" />
        </>
      )}

      {/* Comment input area */}
      <Box
        className={`${styles.view} ${styles.replyInputView}`}
        padding="12"
        style={{'--comment-reply-actions-width': '40px'} as CSSProperties}
      >
        <Box style={{alignSelf: 'flex-start'}}>
          <Avatar size="small" user={employee} />
        </Box>
        <Box ref={replyInputBodyRef} className={styles.replyInputBody}>
          <Box ref={replyInputEditorRef} className={styles.replyInputEditor}>
            <RichTextArea
              ref={editorRef}
              hideToolbar
              editable={!isDeletingLastComment}
              links={false}
              mentions
              commentMentions={commentMentions}
              sanitizeCommentPaste
              disableBorder
              backgroundHighlight="Background Base"
              minHeight={22}
              maxHeight={replyInputMaxHeight}
              onToggleFocused={() => {}}
              placeholder={<Text color="disabled">{thread ? t('reply') : t('add_a_comment')}</Text>}
              source="comments"
              contentClassName={styles.replyInputContent}
              onChange={(value) => {
                const nextValue = value?.toString() ?? '';
                setCommentText(nextValue);
                const allowShrink = nextValue.length < previousReplyInputLengthRef.current;
                previousReplyInputLengthRef.current = nextValue.length;

                if (typeof requestAnimationFrame === 'function') {
                  requestAnimationFrame(() => updateReplyInputWrapHeight({allowShrink}));
                } else {
                  updateReplyInputWrapHeight({allowShrink});
                }
              }}
              autoFocus={autoFocusInput}
            />
          </Box>

          <Box className={styles.replyInputActions}>
            {/* Submit button */}
            <ThemeProvider mode={isPrivate ? (mode === 'bright' ? 'dark' : 'bright') : mode}>
              <ThemeContainer>
                <IconButton
                  variant={isPrivate ? 'filled' : 'opacity'}
                  disabled={!sanitizedCommentText.trim() || isCreating || isDeletingLastComment}
                  onMouseDown={(event: any) => event.preventDefault()}
                  onPointerDown={(e: React.PointerEvent) => e.preventDefault()}
                  onClick={handleSubmit}
                  className={atoms({flexShrink: 0})}
                  aria-label="icon button"
                >
                  <UIIcon src={ArrowUpLine} />
                </IconButton>
              </ThemeContainer>
            </ThemeProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const CommentDisplay = (props: CommentDisplayProps) => {
  if (props.mode === 'preview') {
    return <CommentDisplayPreview thread={props.thread} compact={props.compact} />;
  }

  return <CommentDisplayActive {...props} />;
};
